import { useEffect, useState, type FormEvent } from 'react';
import './educator-interest.css';

type Details = { name: string; email: string; institution: string; role: string; message: string; contactPermission: boolean };
type Request = Details & { requestId: string; website: string };
const empty: Details = { name: '', email: '', institution: '', role: '', message: '', contactPermission: false };
const recoveryKey = 'marvin-educator-interest-pending-v1';
function restore(): Request | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(recoveryKey) || 'null');
    return value && typeof value.requestId === 'string' && value.contactPermission === true &&
      ['name', 'email', 'institution', 'role', 'message', 'website'].every(k => typeof value[k] === 'string') ? value : null;
  } catch { return null; }
}
export function EducatorInterest() {
  const [pending, setPending] = useState(restore);
  const [details, setDetails] = useState<Details>(() => pending || { ...empty });
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [received, setReceived] = useState('');
  useEffect(() => {
    const leave = (event: BeforeUnloadEvent) => {
      if (!received && (pending || details.name || details.email || details.institution || details.role || details.message)) {
        event.preventDefault(); event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', leave);
    return () => window.removeEventListener('beforeunload', leave);
  }, [details, pending, received]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    const request = pending || { ...details, website, requestId: crypto.randomUUID() };
    try { sessionStorage.setItem(recoveryKey, JSON.stringify(request)); }
    catch { setError('Your browser could not retain this request for retry. Keep this page open and enable site storage before submitting. Nothing was sent.'); return; }
    setPending(request); setBusy(true); setError('');
    try {
      const response = await fetch('/api/educator-interest', { method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Marvin-Request': '1' }, body: JSON.stringify(request),
        signal: AbortSignal.timeout(20000) });
      const result = await response.json();
      if (!response.ok) {
        if ([400, 403, 409, 413].includes(response.status)) {
          sessionStorage.removeItem(recoveryKey); setPending(null);
        }
        throw Error(result.error || 'Your request could not be saved. Please try again.');
      }
      if (result.received !== true || result.id !== request.requestId) throw Error('The response could not be confirmed. Retry the saved request.');
      sessionStorage.removeItem(recoveryKey); setPending(null); setDetails({ ...empty }); setReceived(result.id);
    } catch (e) {
      setError(e instanceof Error && e.name !== 'TimeoutError' && e.name !== 'TypeError' ? e.message : 'We could not confirm receipt. Your details are retained in this tab. Retry the saved request to avoid a duplicate.');
    } finally { setBusy(false); }
  }
  return <div className="account-shell educator-interest">
    <header className="account-header">
      <a className="account-brand" href="/">Marvin<span>by Immerso Gnosis</span></a>
      <nav aria-label="Educator navigation"><a href="/">Marvin website</a><a href="/educator">Educator sign in</a></nav>
    </header>
    <main className="account-grid">
      <section className="account-intro">
        <p className="eyebrow">MARVIN FOR EDUCATORS</p>
        <h1>Bring Marvin to your institution.</h1>
        <p className="account-lede">Tell us about your interest in supporting students with Marvin. You don’t need an account to get in touch.</p>
        <ol className="account-steps"><li className="current"><span>1</span>Register your interest</li><li><span>2</span>Discuss institutional approval</li><li><span>3</span>Invite your teaching team</li></ol>
        <div className="interest-boundary"><h2>Institution approval comes first</h2><p>Registering interest does not grant educator access or enrol students. An authorised institutional representative must approve use before a workspace is activated.</p><p>Students retain control of the work they choose to share.</p></div>
      </section>
      <section className="account-form-card" aria-labelledby="interest-title">
        {received ? <div role="status">
          <p className="eyebrow">REQUEST RECEIVED</p><h2 id="interest-title">Your interest is registered.</h2>
          <p>The Marvin team can contact you about your request and the institution approval process.</p>
          <p>This has not created an account or granted access.</p><p className="interest-reference">Reference: {received}</p>
          <a href="/">Return to the Marvin website</a>
        </div> : <>
          <p className="eyebrow">START A CONVERSATION</p><h2 id="interest-title">Register interest</h2>
          <p>All fields are required unless marked optional.</p>
          {pending && <p role="status" className="interest-boundary">A request is saved in this browser tab. Retry it to confirm receipt; the same request will be sent once more without creating a duplicate.</p>}
          <form onSubmit={submit}>
            <fieldset disabled={busy || !!pending}>
              {([
                ['name', 'Your name', 'text', 'name', 120], ['email', 'Work email', 'email', 'email', 254],
                ['institution', 'Institution', 'text', 'organization', 200], ['role', 'Your role', 'text', 'organization-title', 120],
              ] as const).map(([key, label, type, autoComplete, maxLength]) => <div key={key}>
                <label htmlFor={`interest-${key}`}>{label}</label><input id={`interest-${key}`} name={key} type={type} autoComplete={autoComplete}
                  required maxLength={maxLength} minLength={key === 'institution' || key === 'role' ? 2 : 1} value={details[key]}
                  onChange={e => setDetails({ ...details, [key]: e.target.value })} />
              </div>)}
              <label htmlFor="interest-message">How would you like to use Marvin? (optional)</label>
              <textarea id="interest-message" name="message" rows={4} maxLength={2000} value={details.message}
                aria-describedby="interest-message-hint" onChange={e => setDetails({ ...details, message: e.target.value })} />
              <p id="interest-message-hint" className="account-hint">Please don’t include student names, coursework or other private student information.</p>
              <div className="interest-trap" aria-hidden="true"><label htmlFor="interest-website">Leave this blank</label>
                <input id="interest-website" tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} /></div>
              <label className="account-checkbox"><input type="checkbox" required checked={details.contactPermission}
                onChange={e => setDetails({ ...details, contactPermission: e.target.checked })} />Marvin may contact me about this request.</label>
            </fieldset>
            <p className="account-hint">Your details are stored for Marvin staff to review and respond to this enquiry. This does not subscribe you to marketing emails.</p>
            {error && <p role="alert" className="interest-error">{error}</p>}
            <button className="button primary account-submit" type="submit" disabled={busy}>{busy ? 'Sending…' : pending ? 'Retry saved request' : 'Register interest'}</button>
          </form>
          <p className="account-hint">Already invited by your institution? <a href="/educator">Sign in</a>.</p>
        </>}
      </section>
    </main>
  </div>;
}
